import { useEffect, useState } from "react";
import { X } from "lucide-react";

function formatHoursLabel(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "0.0 hrs";
  }
  return `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(numeric)} hrs`;
}

export function AssignUnmappedOpCodeModal({
  visible,
  opCode,
  opDescription,
  hoursAvailable,
  assignableTargets = [],
  isSaving = false,
  onClose,
  onAssign,
}) {
  const [selectedInvProfileDetailId, setSelectedInvProfileDetailId] = useState("");

  useEffect(() => {
    if (!visible) {
      setSelectedInvProfileDetailId("");
      return;
    }
    const firstTarget = assignableTargets[0];
    setSelectedInvProfileDetailId(
      firstTarget?.invProfileDetailId != null ? String(firstTarget.invProfileDetailId) : ""
    );
  }, [visible, opCode, assignableTargets]);

  if (!visible || !opCode) {
    return null;
  }

  const hasTargets = assignableTargets.length > 0;
  const selectedId = Number(selectedInvProfileDetailId);
  const canAssign =
    hasTargets && Number.isFinite(selectedId) && selectedId > 0 && !isSaving;

  const handleAssign = () => {
    if (!canAssign) {
      return;
    }
    onAssign?.(selectedId);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-unmapped-op-code-modal-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5 dark:border-gray-700">
          <div>
            <h2
              id="assign-unmapped-op-code-modal-title"
              className="text-xl font-semibold text-gray-900 dark:text-gray-100"
            >
              Assign Op Code
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {opCode}
              {opDescription ? ` - ${opDescription}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="app-btn-plain rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900 dark:bg-blue-950/30">
            <div className="flex items-center justify-between gap-4 text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Hours to assign</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {formatHoursLabel(hoursAvailable)}
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              All hours for this op code will be assigned to the selected invoice line.
            </p>
          </div>

          <div className="mt-5">
            <label
              htmlFor="assign-unmapped-op-code-invoice-line"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Invoice line
            </label>
            {hasTargets ? (
              <select
                id="assign-unmapped-op-code-invoice-line"
                value={selectedInvProfileDetailId}
                onChange={(e) => setSelectedInvProfileDetailId(e.target.value)}
                disabled={isSaving}
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              >
                {assignableTargets.map((target) => (
                  <option key={target.invProfileDetailId} value={String(target.invProfileDetailId)}>
                    {target.description || `Line ${target.invProfileDetailId}`}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                No labor invoice lines are on this invoice. Add a labor line from the profile
                first.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={!canAssign}
            className="rounded bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? "Assigning…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
